import React from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";

/**
 * Catches render-time errors anywhere below it in the tree so a single
 * broken component doesn't take down the whole app.
 * Keeps the fallback minimal on purpose — relies on Tailwind + v2 CSS vars.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, err: null };
  }

  static getDerivedStateFromError(err) {
    return { hasError: true, err };
  }

  componentDidCatch(err, info) {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", err, info?.componentStack);
    try {
      if (typeof window !== "undefined") {
        window.__lastAppError = { message: String(err?.message || err), stack: err?.stack, componentStack: info?.componentStack };
      }
    } catch (e) { /* noop */ }
  }

  reset = () => {
    this.setState({ hasError: false, err: null });
    try { window.location.reload(); } catch (e) { /* noop */ }
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    const err = this.state.err;
    return (
      <div style={{
        minHeight: "100vh",
        background: "#05070b",
        color: "#e5e7eb",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        fontFamily: "'Figtree', system-ui, sans-serif",
      }}>
        <div style={{ textAlign: "center", maxWidth: 520 }}>
          <div style={{
            width: 64, height: 64, margin: "0 auto 18px",
            display: "flex", alignItems: "center", justifyContent: "center",
            borderRadius: 16,
            background: "linear-gradient(135deg, rgba(239,68,68,0.15), rgba(239,68,68,0.05))",
            border: "1px solid rgba(239,68,68,0.3)",
            color: "#fca5a5",
          }}>
            <AlertTriangle size={28} />
          </div>
          <div style={{ fontSize: 24, fontWeight: 800, marginBottom: 8, letterSpacing: "-0.01em" }}>
            Something went wrong
          </div>
          <div style={{ fontSize: 15, color: "rgba(226,232,240,0.7)", lineHeight: 1.5, marginBottom: 24 }}>
            The app hit an unexpected error. A quick reload usually fixes it. If the problem
            persists, please contact support.
          </div>
          {err?.message && (
            <details style={{
              textAlign: "left", background: "rgba(255,255,255,0.03)",
              border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10,
              padding: "10px 14px", marginBottom: 20,
              fontSize: 12.5, color: "rgba(226,232,240,0.65)",
            }}>
              <summary style={{ cursor: "pointer", fontWeight: 600 }}>Technical details</summary>
              <pre style={{ marginTop: 8, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                {String(err.message)}
              </pre>
            </details>
          )}
          <button
            onClick={this.reset}
            style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              padding: "12px 22px", borderRadius: 999, border: "none",
              background: "linear-gradient(95deg,#10b981,#06b6d4)",
              color: "#03130c", fontWeight: 700, fontSize: 15, cursor: "pointer",
              boxShadow: "0 10px 30px rgba(16,185,129,0.3)",
            }}
          >
            <RefreshCw size={16} /> Reload app
          </button>
        </div>
      </div>
    );
  }
}
